// Team Transcripts Page - Main orchestrator for team transcript functionality
// Route: /teams/:teamId/transcripts

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTeam } from '../contexts/TeamContext';
import { TeamTranscriptsList } from '../components/TeamTranscripts';
import { TeamTranscriptDetail } from '../components/TeamTranscripts';
import type { TeamTranscript, TeamTranscriptDetail as TeamTranscriptDetailType } from '../types/teamTranscripts';

const TeamTranscriptsPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { selectedTeam, teams, isLoading: teamsLoading } = useTeam();

  // State management
  const [selectedTranscript, setSelectedTranscript] = useState<TeamTranscript | null>(null);
  const [selectedTranscriptDetail, setSelectedTranscriptDetail] = useState<TeamTranscriptDetailType | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Validate team access
  useEffect(() => {
    if (!teamsLoading && teams && teams.length > 0 && teamId) {
      const team = teams.find(t => t.id === teamId);
      if (!team) {
        // Team not found or user doesn't have access
        navigate('/');
        return;
      }
    }
  }, [teamId, teams, teamsLoading, navigate]);

  // Handle transcript selection
  const handleSelectTranscript = (transcript: TeamTranscript) => {
    setSelectedTranscript(transcript);
    setSelectedTranscriptDetail(null); // Clear previous detail
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedTranscript(null);
    setSelectedTranscriptDetail(null);
  };

  // Handle transcript updates (notes/action items saved)
  const handleTranscriptUpdated = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Loading state
  if (teamsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  // Team not found
  if (!teamId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Team Not Found</h2>
          <p className="text-gray-600 mb-4">The requested team could not be found.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple-dark transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900 transition-colors"
            title="Back to Home"
          >
            ← Back
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Team Transcripts</h1>
            {selectedTeam && (
              <p className="text-sm text-gray-600">{selectedTeam.name}</p>
            )}
          </div>
        </div>
        
        {selectedTranscript && (
          <button
            onClick={handleBackToList}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            ← Back to List
          </button>
        )}
      </header>

      {/* Main Content - Two Column Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Column - Transcript List (30%) */}
        <div className={`${selectedTranscript ? 'w-1/3' : 'w-full'} border-r border-gray-200 bg-white transition-all duration-300`}>
          <TeamTranscriptsList
            teamId={teamId}
            selectedTranscript={selectedTranscript}
            onSelectTranscript={handleSelectTranscript}
            refreshTrigger={refreshTrigger}
          />
        </div>

        {/* Right Column - Transcript Detail (70%) */}
        {selectedTranscript && (
          <div className="w-2/3 bg-white">
            <TeamTranscriptDetail
              teamId={teamId}
              transcript={selectedTranscript}
              transcriptDetail={selectedTranscriptDetail}
              onTranscriptDetailLoaded={setSelectedTranscriptDetail}
              onTranscriptUpdated={handleTranscriptUpdated}
            />
          </div>
        )}

        {/* Empty State when no transcript selected */}
        {!selectedTranscript && (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Transcript</h3>
              <p className="text-gray-600">Choose a transcript from the list to view its details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamTranscriptsPage;